// The aisle sequence a Shopping list ordered by category is shown in (g81). The identifiers are
// stable names that never change meaning; the ranks are the order, kept apart
// from the names so a future sequence can move an aisle without renaming it.
//
// The walk runs from the entrance to the checkout, with the departments named
// as on coop.ch (settled with Andre, g61 and g80): flowers stand at the door,
// the fresh aisles and the chilled ready meals follow, then the dry goods and
// drinks, with wine and spirits apart from the soft drinks and beer. The
// non-food aisles come next, frozen goods go in the trolley last, and the
// kiosk is at the checkout. Other is always last.
//
// An identifier that would change meaning is retired instead: g80 split
// "baby-pet" into "baby" and "pet", and the old name is not reused. A label
// is text people keep, since the fill writes it into an item's category
// (g79, g81), so a label that carries over keeps its exact spelling.

export type CategoryId =
  | "flowers"
  | "produce"
  | "bakery"
  | "meat-fish"
  | "dairy"
  | "ready-meals"
  | "pantry"
  | "hot-drinks"
  | "snacks"
  | "drinks"
  | "wine-spirits"
  | "household"
  | "pet"
  | "personal-care"
  | "baby"
  | "frozen"
  | "kiosk"
  | "other";

export interface Category {
  readonly id: CategoryId;
  readonly rank: number;
  readonly label: string;
}

export const CATEGORIES: readonly Category[] = Object.freeze([
  { id: "flowers", rank: 0, label: "Flowers & plants" },
  { id: "produce", rank: 1, label: "Fruit & vegetables" },
  { id: "bakery", rank: 2, label: "Bakery" },
  { id: "meat-fish", rank: 3, label: "Meat & fish" },
  { id: "dairy", rank: 4, label: "Dairy, eggs & alternatives" },
  { id: "ready-meals", rank: 5, label: "Ready meals & deli" },
  { id: "pantry", rank: 6, label: "Pantry & cooking" },
  { id: "hot-drinks", rank: 7, label: "Hot drinks" },
  { id: "snacks", rank: 8, label: "Snacks & sweets" },
  { id: "drinks", rank: 9, label: "Drinks" },
  { id: "wine-spirits", rank: 10, label: "Wine & spirits" },
  { id: "household", rank: 11, label: "Household" },
  { id: "pet", rank: 12, label: "Pet" },
  { id: "personal-care", rank: 13, label: "Personal care" },
  { id: "baby", rank: 14, label: "Baby & child" },
  { id: "frozen", rank: 15, label: "Frozen" },
  { id: "kiosk", rank: 16, label: "Kiosk & tobacco" },
  { id: "other", rank: 17, label: "Other" },
].map((category) => Object.freeze(category as Category)));

const RANKS = new Map<CategoryId, number>(CATEGORIES.map((category) => [category.id, category.rank]));

export function rankOf(id: CategoryId): number {
  const rank = RANKS.get(id);
  if (rank === undefined) throw new Error(`unknown grocery category: ${id}`);
  return rank;
}
